import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NavLink, useNavigate, useParams} from 'react-router-dom';
import {EditOutlined, SaveOutlined} from '@ant-design/icons';
import {Button, Card, Col, Descriptions, Form, Grid, Input, Row, Select, Space, Tag, Typography} from 'antd';
import {AttachmentsDropzone} from 'components/AttachmentsDropzone';
import {Shell} from 'components/Shell';
import {CommentContainer} from 'components/ui/CommentContainer';
import {getIsMobile, getIsTablet} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {AppRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {TaskState} from 'store/tasks/TaskState';
import {ServiceStatus} from '../../../../../../PRMGApiClient';
import {CardTitle, DatetimeColumn, DescriptionLabel, getStatusColor} from '../utils/ServicesUtils';

const {Text} = Typography;

export const TaskDisplayView: React.FC = observer(() => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {taskId} = useParams();

  const [state] = useState(() => new TaskState());
  const [form] = Form.useForm();

  const screen = Grid.useBreakpoint();

  const isMobile = useMemo(() => getIsMobile(screen), [screen]);
  const isTablet = useMemo(() => getIsTablet(screen), [screen]);

  useEffect(() => state.loadTask(Number(taskId)), [taskId, state]);

  return (
    <Shell
      backLink
      title={state.item.name}
      actionButtons={[
        <Button
          key="edit"
          size="large"
          className="shell-btn"
          onClick={() => navigate(`${AppRoutePaths.tasks.url}/${taskId}/edit`)}
        >
          {t('Edit Task')}
        </Button>,
      ]}
    >
      <Row gutter={16}>
        <Col className="flex flex-col gap-4" span={isMobile ? 24 : 10}>
          <Card title={<CardTitle text={t('Service task')} />}>
            <Descriptions column={isTablet ? 1 : 2} colon={false} layout="vertical">
              <Descriptions.Item label={<DescriptionLabel text={t('Service action')} />} span={isTablet ? 1 : 2}>
                <NavLink to={AppRoutePaths.services.url + `/${state.serviceAction?.id}`}>
                  <Button type="link" className="p-0 m-0 h-max">
                    {state.serviceAction?.name}
                  </Button>
                </NavLink>
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Start date')} />}>
                {DatetimeColumn(state.item.startDateTime)}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('End date')} />}>
                {DatetimeColumn(state.item.endDateTime)}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Assigned to')} />}>
                {state.item.assignee?.firstName} {state.item.assignee?.lastName}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Status')} />} className="flex gap-2 items-center">
                {state.statusEdit ? (
                  <>
                    <Select
                      defaultValue={state.item.status}
                      size="small"
                      className="mr-2 w-32"
                      onChange={(value) => state.setStatus(value)}
                      options={Object.keys(ServiceStatus).map((key) => ({
                        value: key,
                        label: key,
                      }))}
                    />
                    <Button
                      type="link"
                      size="small"
                      icon={<SaveOutlined />}
                      onClick={() => {
                        state.setStatusEdit(false);
                        state.updateStatus();
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Tag color={getStatusColor(state.item.status)}>{state.item.status}</Tag>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => state.setStatusEdit(true)}
                    />
                  </>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={<DescriptionLabel text={t('Description')} />} span={isTablet ? 1 : 2}>
                {state.item.description}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title={<CardTitle text={t('Attachments')} />} className="relative">
            <AttachmentsDropzone
              attachments={state.item.attachments ?? []}
              addAttachments={(files) => state.addAttachments(files)}
              removeAttachment={(id) => state.removeAttachment(id)}
              isLoading={state.isLoading}
            />
          </Card>
        </Col>
        <Col span={isMobile ? 24 : 14} className="mt-4 md:mt-0">
          <Card title={<CardTitle text={t('Comments')} />} className="h-full">
            <Space direction="vertical" size="large" className="w-full">
              <Form
                form={form}
                onFinish={(values) => {
                  state.addComment(values)?.then(() => form.resetFields());
                }}
                layout="vertical"
              >
                <Form.Item
                  name="comment"
                  required
                  rules={[
                    {required: true, message: String(t('Comment cannot be empty'))},
                    {max: 500, message: String(t('Max 500 characters'))},
                  ]}
                >
                  <Input.TextArea rows={4} placeholder={t('Add comment')} />
                </Form.Item>

                <Button type="primary" htmlType="submit">
                  {String(t('Add comment'))}
                </Button>
              </Form>
              <Space direction="vertical" size="small" className="w-full">
                {state.item.comments?.length ? (
                  state.item.comments?.map((item) => (
                    <CommentContainer
                      key={item.id}
                      comment={item}
                      isAssignedToMe={appState.user?.id === item.authorId}
                      onDelete={() => item.id && state.removeComment(item.id)}
                      onEdit={(comment: {commentId: number; value: string}) => state.editComment(comment)}
                    />
                  ))
                ) : (
                  <Text italic>{t('No comments')}</Text>
                )}
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>
    </Shell>
  );
});
